import { useEffect, useState, useRef } from 'react'
import { useCloseOnEscape, useCheckBanner } from 'shared-utilities'
import { useOnClickOutside } from 'usehooks-ts'
import { usePathPrefix } from '../../hooks/usePathPrefix'
import { Link } from 'gatsby'
import { getCustomBackLinks, customRoutes } from '../../utils/routing'
import { getPathname } from '../../utils/url_helpers'
import {
  ContentType,
  contentSortParams,
  createSearchPageRoute,
} from 'shared-utilities'
import './style.scss'
import LinkComponent from '../LinkComponent/LinkComponent'

interface IBreadcrumbsProps {
  pageTitle?: string
  categories?: string[]
}

interface IBackURL {
  path: string
  customPath?: string
  label: string
}

const customBackLinks = getCustomBackLinks()
const skipPaths = [`/demos`, `/demonstrations`]

const Breadcrumbs = ({ pageTitle, categories }: IBreadcrumbsProps) => {
  const [isBannerPresent, setIsBannerPresent] = useState<boolean>(false)
  const [showDropdown, setShowDropdown] = useState<boolean>(false)
  const [breadcrumbs, setBreadcrumbs] = useState<IBackURL[]>([])
  const [category, setCategory] = useState<IBackURL>()

  const pathname = getPathname() as string
  const pathPrefix = usePathPrefix()

  useCheckBanner(setIsBannerPresent)
  useCloseOnEscape(showDropdown, setShowDropdown)

  const ref = useRef(null)
  useOnClickOutside(ref, () => setShowDropdown(false))

  useEffect(() => {
    let routes: string[] = []
    // Remove ending forward slash if it exists
    let originalPath = pathname.replace(/\/$/, '')
    // For PR previews remove pathPrefix as it's getting added twice
    if (originalPath.includes(pathPrefix)) {
      originalPath = originalPath.replace(pathPrefix, '')
    }
    // if custom back path exists
    if (customBackLinks[originalPath]) {
      routes = customBackLinks[originalPath].split(/(?=\/)/)
    } else {
      // Create path items array
      routes = originalPath.split(/(?=\/)/)
      // remove last part of url
      routes.pop()
    }
    // Create breadcrumbs array
    const matchedBreadcrumbs = customRoutes.filter((route) => {
      return routes.includes(route.path)
    })
    matchedBreadcrumbs.length &&
      setBreadcrumbs([...breadcrumbs, ...matchedBreadcrumbs])

    // Identify category path if category exists,
    // mostly a case for demos
    if (categories?.length) {
      const matchedCategory = customRoutes.filter((route) => {
        return categories[0] === route.label
      })

      matchedCategory.length && setCategory(matchedCategory[0])
    }
  }, [pathname])

  useEffect(() => {
    // Adjust breadcrumbs for demos' routing
    if (skipPaths.includes(breadcrumbs[1]?.path)) {
      const updatedBreadcrumb = breadcrumbs.slice(1)
      setBreadcrumbs(updatedBreadcrumb)
    }
  }, [breadcrumbs])

  return (
    <>
      <span
        className={`Breadcrumbs ${
          isBannerPresent ? 'Breadcrumbs--bannerVisible' : ''
        }`}
        data-testid="breadcrumbs-test"
      >
        {(breadcrumbs.length > 1 || category) && (
          <span ref={ref}>
            <span className="md:hidden">
              <button
                onClick={() => setShowDropdown(!showDropdown)}
                className="Breadcrumbs__ellipsis"
                aria-label="Toggle navigation dropdown"
                aria-expanded={showDropdown}
              >
                ...
              </button>{' '}
              <span className="Breadcrumb__forwardSlash">/ </span>
            </span>
            {showDropdown && (
              <div className="Breadcrumbs__dropdown">
                <ul>
                  {breadcrumbs.map((breadcrumb) => (
                    <li key={breadcrumb.label}>
                      <Link
                        to={`${breadcrumb.path !== '/qml' ? '/qml' : ''}${
                          breadcrumb.customPath
                            ? breadcrumb.customPath
                            : breadcrumb.path
                        }`}
                      >
                        {breadcrumb.label}
                      </Link>
                    </li>
                  ))}
                  {category && (
                    <li key={category.label}>
                      <Link
                        to={`/qml${
                          pathname.includes('/qml/demos/')
                            ? '/demonstrations'
                            : ''
                        }${category.path}`}
                      >
                        {category.label}
                      </Link>
                    </li>
                  )}
                </ul>
              </div>
            )}
          </span>
        )}
        {breadcrumbs.map((breadcrumb) => (
          <span
            key={breadcrumb.label}
            className={breadcrumbs.length > 1 || category ? 'Breadcrumb' : ''}
          >
            <Link
              to={`${breadcrumb.path !== '/qml' ? '/qml' : ''}${
                breadcrumb.customPath ? breadcrumb.customPath : breadcrumb.path
              }`}
            >
              {breadcrumb.label}
            </Link>{' '}
            <span className="Breadcrumb__forwardSlash">/ </span>
          </span>
        ))}
        {category && (
          <span
            className="Breadcrumb__category"
            data-testid="breadcrumb-category"
          >
            <LinkComponent
              href={createSearchPageRoute({
                category: category.label,
                contentType: ContentType.DEMO,
                sort: contentSortParams.PUBLICATION_DATE_DESC,
              })}
            >
              {category.label}
            </LinkComponent>{' '}
            <span className="Breadcrumb__forwardSlash">/ </span>
          </span>
        )}
        {pageTitle && <span className="pageTitle">{pageTitle}</span>}
      </span>
    </>
  )
}

export default Breadcrumbs
